import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit, X, Check, Loader2, Globe, Link as LinkIcon } from 'lucide-react';
import { scrapeWebsite } from '../../apis/aiChatbotAPI';

const AiChatbotKnowledgeBaseField = ({
  value,
  onChange,
  onSave,
  placeholder,
  label,
  helpText,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Ensure value is always a string
  const safeValue = typeof value === 'string' ? value : (value || '');
  const [tempValue, setTempValue] = useState(safeValue);
  const [justSaved, setJustSaved] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState('');
  
  // Update tempValue when value prop changes
  useEffect(() => {
    const newSafeValue = typeof value === 'string' ? value : (value || '');
    if (!isEditing) {
      setTempValue(newSafeValue);
    }
  }, [value, isEditing]);

  const handleEdit = () => {
    const safeValue = typeof value === 'string' ? value : (value || '');
    setTempValue(safeValue);
    setIsEditing(true);
    setJustSaved(false);
  };

  const handleCancel = () => {
    const safeValue = typeof value === 'string' ? value : (value || '');
    setTempValue(safeValue);
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Validate knowledge base length
    if (tempValue.length > 4000) {
      alert("Knowledge base is too long. Maximum 4000 characters allowed. Please reduce the content.");
      return;
    }

    setIsSaving(true);
    try {
      onChange({ target: { value: tempValue } });
      await onSave(tempValue);
      setIsEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleScrapeWebsite = async () => {
    if (!websiteUrl || websiteUrl.trim().length === 0) {
      setScrapeError("Please enter a valid URL");
      return;
    }

    setIsScraping(true);
    setScrapeError('');
    
    try {
      const response = await scrapeWebsite(websiteUrl.trim());
      
      if (response.data.success) {
        // Replace content with AI-processed knowledge base
        // The AI has already cleaned and structured the content
        const newContent = response.data.content;
        const pagesInfo = response.data.pagesScraped 
          ? `\n\n[Knowledge base created from ${response.data.pagesScraped} pages of ${response.data.url}]`
          : '';
        
        const existingContent = tempValue.trim();
        
        let combinedContent;
        if (existingContent) {
          // Append with separator
          combinedContent = `${existingContent}\n\n---\n\n${newContent}${pagesInfo}`;
        } else {
          // Replace if empty
          combinedContent = `${newContent}${pagesInfo}`;
        }
        
        // Truncate to 3000 characters if needed
        const MAX_LENGTH = 4000;
        if (combinedContent.length > MAX_LENGTH) {
          combinedContent = combinedContent.substring(0, MAX_LENGTH);
          alert(`Website content was truncated to ${MAX_LENGTH} characters. You can edit it below if needed.`);
        }
        
        setTempValue(combinedContent);
        setWebsiteUrl('');
        setShowUrlInput(false);
        setScrapeError('');
      } else {
        setScrapeError(response.data.error || "Failed to scrape website");
      }
    } catch (error) {
      console.error("Error scraping website:", error);
      setScrapeError(error.response?.data?.error || "Failed to fetch website content. Please check the URL and try again.");
    } finally {
      setIsScraping(false);
    }
  };

  const getTruncatedText = (text, maxLines = 3) => {
    if (!text) return placeholder;
    // Ensure text is a string
    const textStr = typeof text === 'string' ? text : String(text || '');
    const lines = textStr.split("\n");
    if (lines.length > maxLines && !isExpanded) {
      return lines.slice(0, maxLines).join("\n") + "...";
    }
    return textStr;
  };

  const shouldShowExpandButton = value && typeof value === 'string' && value.split("\n").length > 3;

  if (isEditing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="font-medium text-gray-900">{label}</label>
          <div className="flex items-center space-x-2">
            <motion.button
              onClick={handleCancel}
              className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <X size={16} />
            </motion.button>
            <motion.button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-3 py-1 rounded text-sm font-medium shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/40 ${
                isSaving
                  ? "bg-[#048AAD]/60 text-white cursor-not-allowed"
                  : "bg-[#048AAD] text-white hover:bg-[#048AAD]/90"
              }`}
              whileHover={!isSaving ? { scale: 1.02 } : {}}
              whileTap={!isSaving ? { scale: 0.98 } : {}}
            >
              {isSaving ? (
                <>
                  <Loader2 size={14} className="inline mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={14} className="inline mr-1" />
                  Save
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Website URL Input */}
        <div className="space-y-2">
          {!showUrlInput ? (
            <motion.button
              onClick={() => setShowUrlInput(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[#01CC54] bg-[#01CC54]/10 hover:bg-[#01CC54]/20 rounded-lg border border-[#01CC54]/30 transition-colors"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <Globe size={16} />
              Fetch from Website URL
            </motion.button>
          ) : (
            <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <LinkIcon size={16} className="text-blue-600" />
                <label className="text-sm font-medium text-gray-900">Website URL</label>
              </div>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => {
                    setWebsiteUrl(e.target.value);
                    setScrapeError('');
                  }}
                  placeholder="https://example.com or example.com"
                  className="flex-1 rounded-lg px-3 py-2 text-sm text-gray-900 border border-gray-300 bg-white focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                  disabled={isScraping}
                />
                <motion.button
                  onClick={handleScrapeWebsite}
                  disabled={isScraping || !websiteUrl.trim()}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    isScraping || !websiteUrl.trim()
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-[#01CC54] text-white hover:bg-[#01CC54]/90"
                  }`}
                  whileHover={!isScraping && websiteUrl.trim() ? { scale: 1.02 } : {}}
                  whileTap={!isScraping && websiteUrl.trim() ? { scale: 0.98 } : {}}
                >
                  {isScraping ? (
                    <>
                      <Loader2 size={14} className="inline mr-1 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <Globe size={14} className="inline mr-1" />
                      Fetch
                    </>
                  )}
                </motion.button>
                <button
                  onClick={() => {
                    setShowUrlInput(false);
                    setWebsiteUrl('');
                    setScrapeError('');
                  }}
                  className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isScraping}
                >
                  <X size={16} />
                </button>
              </div>
              {scrapeError && (
                <p className="text-xs text-red-600 mt-1">{scrapeError}</p>
              )}
              <p className="text-xs text-gray-600">
                Paste a website URL to automatically scrape all pages, extract meaningful content, and create an AI-processed knowledge base. The system will discover and scrape multiple pages, then use AI to structure the content.
              </p>
            </div>
          )}
        </div>

        <textarea
          value={tempValue}
          onChange={(e) => {
            const value = e.target.value;
            // Allow changes if within limit OR if deleting (new value is shorter)
            if (value.length <= 4000 || value.length < tempValue.length) {
              setTempValue(value);
            }
          }}
          maxLength={4000}
          rows={6}
          className={`w-full rounded-lg p-3 text-sm text-gray-900 placeholder-gray-500 border bg-white focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition resize-y min-h-[120px] max-h-[300px] ${
            tempValue.length > 4000 
              ? "border-red-500" 
              : tempValue.length > 3600 
              ? "border-yellow-500" 
              : "border-gray-200"
          }`}
          placeholder={placeholder}
          autoFocus
        />

        <div className="flex justify-between items-center">
          {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
          <p className={`text-xs font-semibold ml-auto ${
            tempValue.length > 4000 
              ? "text-red-500" 
              : tempValue.length > 3600 
              ? "text-yellow-600" 
              : "text-gray-600"
          }`}>
            {tempValue.length}/4000
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="font-medium text-gray-900">{label}</label>
        <motion.button
          onClick={handleEdit}
          className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded transition focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          <Edit size={16} />
        </motion.button>
      </div>

      <AnimatePresence>
        <motion.div
          className="rounded-lg border bg-gray-50 border-gray-200 transition-all duration-200"
          initial={justSaved ? { scale: 1.02 } : {}}
          animate={justSaved ? { scale: 1 } : {}}
          transition={{ duration: 0.2 }}
        >
          {justSaved && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center text-emerald-300 text-sm p-3 pb-0"
            >
              <Check size={14} className="mr-1" />
              Saved successfully!
            </motion.div>
          )}

          <div className="p-3">
            <div
              className={`text-sm whitespace-pre-wrap ${
                safeValue ? "text-gray-900" : "text-gray-500"
              }`}
            >
              {getTruncatedText(safeValue)}
            </div>

            {shouldShowExpandButton && (
              <motion.button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 text-xs text-[#01CC54] hover:text-[#01CC54]/80 font-medium focus:outline-none focus:ring-2 focus:ring-[#01CC54]/30 rounded px-1"
                whileHover={{ scale: 1.02 }}
              >
                {isExpanded ? "Show Less" : "Show More"}
              </motion.button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {helpText && <p className="text-xs text-gray-400">{helpText}</p>}
    </div>
  );
};

export default AiChatbotKnowledgeBaseField;

